import React, { useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import Nav from './Nav';
import HeroSection from './HeroSection';
import StatsCounter from './StatsCounter';
import BenefitsGrid from './BenefitsGrid';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Lazy load ALL non-critical components for better performance
const LazyBrandStatsSection = lazy(() => import('./BrandStatsSection'));
const LazyReviewsSection = lazy(() => import('./ReviewsSection'));
const LazyAIHowItWorks = lazy(() => import('./AIHowItWorks'));
const LazyQualitativeResearchSection = lazy(() => import('./QualitativeResearchSection'));
const LazyFileFormatsSection = lazy(() => import('./FileFormatsSection'));
const LazyScrollWrap = lazy(() => import('./ScrollWrap'));
const LazyFeaturesGrid = lazy(() => import('./FeaturesGrid'));
const LazyFAQSection = lazy(() => import('./FAQSection'));
const LazyFooter = lazy(() => import('./Footer'));

// Loading component
const LoadingSection = () => (
  <div style={{ 
    height: '200px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    background: '#f8f9fa' 
  }}>
    <div style={{ color: '#6c757d', fontSize: '14px' }}>Loading...</div>
  </div>
);

function HomePage() {
  const { lang } = useParams();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'en' || lang === 'ar')) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);

  // Initialize AOS for scroll animations
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true,
      offset: 100
    });
  }, []);

  // SEO content optimized for 'audiosum' keyword 
  const seoContent = {
    en: {
      title: "Audiosum | AI Summary, Document, Audio Summary & Video Transcription",
      description: "Convert audio and video to text with 99%+ accuracy in 95+ languages. AI-powered summary and transcription made easy with AI summary tool.",
      keywords: "audiosum, audio transcription, AI transcription, speech to text, audio summarization, AI audio analysis"
    },
    ar: {
      title: "لخص - أداة تلخيص بالذكاء الاصطناعي | تحويل الصوت والفيديو إلى نص مجاناً",
      description: "لخص - منصة تلخيص متطورة لتحويل الصوت إلى نص مجاناً. أداة تلخيص ذكية بالذكاء الاصطناعي لمعالجة المحتوى واستخراج تلخيص دقيق في 95+ لغة.",
      keywords: "لخص، تلخيص، أداة تلخيص، تحويل الصوت، تحويل بالذكاء الاصطناعي، تحويل الكلام إلى نص، تلخيص الصوت، تحليل الصوت"
    }
  };

  const currentLang = lang || 'en';

  return (
    <div>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        keywords={seoContent[currentLang].keywords}
      />
  <Nav />
  <HeroSection />
      
      {/* Stats Counter Section */}
      <StatsCounter 
        showHeading={true}
        title="Trusted by Thousands Worldwide"
        subtitle="Helping students and professionals save thousands of hours of manual work"
      />
      
      <BenefitsGrid />
      
      <Suspense fallback={<LoadingSection />}>
        <LazyAIHowItWorks />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyBrandStatsSection />
      </Suspense>
      
      {/* Keep ScrollWrap but remove SliderWrap to optimize word count */}
      <Suspense fallback={<LoadingSection />}>
        <LazyScrollWrap />
      </Suspense>
      
      <div style={{ marginBottom: '2rem' }}></div>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyQualitativeResearchSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFeaturesGrid />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFileFormatsSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFAQSection />
      </Suspense>
      
      {/* Reviews Section - Positioned after FAQ section */}
      <div className="container px-4" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
        <Suspense fallback={<LoadingSection />}>
          <LazyReviewsSection />
        </Suspense>
      </div>
      
      {/* Simple CTA Section with Features */}
      <div className="container-fluid mb-5" style={{ 
        background: 'linear-gradient(135deg, #F8FAFC 0%, #EDE9FE 100%)', 
        padding: '80px 0' 
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="fw-bold mb-4" style={{ color: '#0F172A', fontSize: '2.5rem', fontFamily: '"Playfair Display", serif' }}>
                {language === 'ar' ? 'ابدأ تجربتك المجانية اليوم' : 'Start Your Free Trial Today'}
              </h2>
              <p className="lead mb-5" style={{ color: '#64748B', fontSize: '1.2rem', fontFamily: '"Inter", sans-serif' }}>
                {language === 'ar' 
                  ? 'اكتشف قوة معالجة المستندات الذكية مع تجربة مجانية لمدة 7 أيام • خطط بأسعار معقولة'
                  : 'Discover the power of intelligent document processing with a 7-day free trial • Affordable plans'
                }
              </p>
              
              {/* Feature Cards */}
              <div className="row mb-5">
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#6C3AED' }}>
                      <i className="fas fa-calendar-check text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                      {language === 'ar' ? 'تجربة مجانية 7 أيام' : '7-Day Free Trial'}
                    </h6>
                  </div>
                </div>
                
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#6C3AED' }}>
                      <i className="fas fa-clock text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                      {language === 'ar' ? 'دقائق للبدء' : 'Minutes to get started'}
                    </h6>
                  </div>
                </div>
                
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#6C3AED' }}>
                      <i className="fas fa-headset text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                      {language === 'ar' ? 'دعم فني على مدار الساعة' : '24/7 technical support'}
                    </h6>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <a 
                href={`https://audiosum.app/signup?lang=${language}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-lg px-5"
                style={{ 
                  borderRadius: '50px',
                  background: '#6C3AED',
                  border: 'none',
                  padding: '14px 40px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 30px rgba(108, 58, 237, 0.3)',
                  textDecoration: 'none',
                  display: 'inline-block',
                  fontFamily: '"Inter", sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5B21B6';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(108, 58, 237, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#6C3AED';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 30px rgba(108, 58, 237, 0.3)';
                }}
              >
                {language === 'ar' ? 'ابدأ مجاناً' : 'Start Free Trial'}
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFooter />
      </Suspense>
    </div>
  );
}

export default HomePage;